import {
  SignInWithGoogle,
  SignIn,
  SignInWithFacebook,
  SignInWithApple,
  TwitterBrowserLink,
  SignInWithTwitter,
  RequestPasswordReset,
  VerifyPasswordResetCode,
  ResetPassword,
  AlertInput,
} from './types';
import { actions } from './slices';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { ApiMethod } from '@wajeez-web/stores/base';
import {
  Server,
  identityClient,
  publicIdentityClient,
} from '@wajeez-web/core/api-base';
import { CommonActions, AlertType } from '@wajeez-web/stores/common';

import {
  getUser,
  removeUser,
  setUser,
  updateUser,
} from './TokenHelper/TokenStorageManager';
import { AxiosInstance, CancelToken } from 'axios';
import {
  getDeviceInfo,
  thunkErrorHandler,
  thunkResponseCheckerSdk,
} from '@wajeez-web/core/utils';

export const name = 'auth';

const deviceDetails = getDeviceInfo();

export const validateEmail: ApiMethod<
  Server.EmailValidationInput & AlertInput,
  void
> = async (data, { dispatch, rejectWithValue }) => {
  try {
    dispatch(CommonActions.showLoading({ show: true }));

    const client = new Server.Server_Account_Client(undefined, identityClient);

    const response = await client.account_emailValidation_post_V1(data);

    if (response.success) {
      dispatch(CommonActions.hideLoading());
    }

    rejectWithValue(response.errors);
  } catch (error: any) {
    dispatch(CommonActions.hideLoading());

    const errors = { ...error.response }.data;

    dispatch(
      CommonActions.setAlert({
        for: data.alert?.for,
        show: true,
        type: AlertType.WARNING,
        messages: JSON.parse(errors).errors || [
          { message: 'Something went wrong' },
        ],
      })
    );

    rejectWithValue(errors);
  }
};

export const validateEmailAsync = createAsyncThunk(
  name + '/validateEmail',
  validateEmail
);

export interface RequestEmailVerificationInput extends AlertInput {
  cancelToken?: CancelToken | undefined;
}

export const requestEmailVerification: ApiMethod<
  RequestEmailVerificationInput,
  void
> = async (data, { dispatch, rejectWithValue }) => {
  try {
    dispatch(CommonActions.clearAlert());

    dispatch(CommonActions.showLoading({ show: true }));

    const client = new Server.Server_Account_Client(undefined, identityClient);

    const response = await client.account_requestEmailVerification_post_V1(
      data.cancelToken
    );

    if (response.success) {
      dispatch(
        CommonActions.setAlert({
          for: data.alert?.for,
          show: true,
          type: AlertType.SUCCESS,
        })
      );

      dispatch(CommonActions.hideLoading());
    } else {
      rejectWithValue(response.errors);
    }
  } catch (error: any) {
    if (error.message !== 'canceled') {
      const errors = error.response && JSON.parse(error.response)?.errors;

      dispatch(
        CommonActions.setAlert({
          show: true,
          for: data.alert?.for,
          type: AlertType.WARNING,
          messages: errors || [{ message: 'Something went wrong' }],
        })
      );

      rejectWithValue(errors);
    } else {
      rejectWithValue(error);
    }
  }
};

export const requestEmailVerificationAsync = createAsyncThunk(
  name + '/requestEmailVerification',
  requestEmailVerification
);

export const requestDelete: ApiMethod<AlertInput, void> = async (
  data,
  { dispatch, rejectWithValue }
) => {
  try {
    dispatch(CommonActions.showLoading({ show: true }));

    const client = new Server.Server_Account_Client(undefined, identityClient);

    const response = await client.account_requestDelete_delete_V1();

    if (response.success) {
      dispatch(CommonActions.hideLoading());

      removeUser();

      dispatch(actions.resetAuthWithAction({ isAccountDeleted: true }));
    }

    return rejectWithValue(response.errors);
  } catch (error: any) {
    dispatch(CommonActions.hideLoading());

    const errors = { ...error.response }.data;

    dispatch(
      CommonActions.setAlert({
        for: data.alert?.for,
        show: true,
        type: AlertType.WARNING,
        messages: JSON.parse(errors).errors || [
          { message: 'Something went wrong' },
        ],
      })
    );

    return rejectWithValue(errors);
  }
};

export const requestDeleteAsync = createAsyncThunk(
  name + '/requestDelete',
  requestDelete
);

export const changePassword: ApiMethod<
  Server.ChangeMyPasswordInput & AlertInput,
  void
> = async (data, { dispatch, rejectWithValue }) => {
  return thunkErrorHandler(
    async () => {
      const client = new Server.Server_Account_Client(
        undefined,
        identityClient
      );

      const response = await client.account_changePassword_post_V1(data);

      if (response.success) {
        dispatch(
          CommonActions.setAlert({
            for: data.alert?.for,
            show: true,
            type: AlertType.SUCCESS,
          })
        );

        return;
      }

      return rejectWithValue(response.errors);
    },
    dispatch,
    rejectWithValue
  );
};

export const changePasswordAsync = createAsyncThunk(
  name + '/changePassword',
  changePassword
);

export const signInB2bByToken: ApiMethod<
  Server.B2BLoginInput,
  Server.AuthenticateOutput,
  void
> = async (data, { dispatch, rejectWithValue }) => {
  return thunkErrorHandler(
    async () => {
      const client = new Server.Server_Account_Client(
        undefined,
        identityClient
      );

      const response = await client.account_authenticate_b2b_post_V1({
        ...data,
        deviceDetails,
      });

      if (response.success && response.data) {
        return response.data;
      }

      return rejectWithValue(response.errors);
    },
    dispatch,
    rejectWithValue,
    'page',
    () => {
      window.location.href = '/login/b2b';
    }
  );
};

export const signInB2bByTokenAsync = createAsyncThunk(
  name + '/signInB2bByToken',
  signInB2bByToken
);

export const changeEmail: ApiMethod<
  Server.ChangeEmailInput & AlertInput,
  string
> = async (data, { dispatch, rejectWithValue }) => {
  try {
    const client = new Server.Server_Account_Client(undefined, identityClient);

    const response = await client.account_changeEmail_post_V1(data);

    if (response.success) {
      const user = getUser();

      updateUser({ ...user, email: data.newEmail });

      dispatch(
        CommonActions.setAlert({
          for: data.alert?.for,
          show: true,
          type: AlertType.SUCCESS,
        })
      );

      return data.newEmail;
    }

    return rejectWithValue(response.errors);
  } catch (error: any) {
    const errors = { ...error.response }.data;

    dispatch(
      CommonActions.setAlert({
        for: data.alert?.for,
        show: true,
        type: AlertType.WARNING,
        messages: JSON.parse(errors).errors || [
          { message: 'Something went wrong' },
        ],
      })
    );

    return rejectWithValue(errors);
  }
};

export const changeEmailAsync = createAsyncThunk(
  name + '/changeEmail',
  changeEmail
);

export const updateUserName: ApiMethod<
  Server.UpdateUserNameInput & AlertInput,
  Server.UserDetailsOutput
> = async (data, { dispatch, rejectWithValue }) => {
  try {
    const client = new Server.Server_Account_Client(undefined, identityClient);

    const response = await client.account_updateName_put_V1({
      name: data.name,
    });

    if (response.success && response.data) {
      const user = getUser();

      updateUser({ ...user, name: response.data.name });

      dispatch(
        CommonActions.setAlert({
          for: data.alert?.for,
          show: true,
          type: AlertType.SUCCESS,
        })
      );

      return response.data;
    }

    return rejectWithValue(response.errors);
  } catch (error: any) {
    const errors = { ...error.response }.data;

    dispatch(
      CommonActions.setAlert({
        for: data.alert?.for,
        show: true,
        type: AlertType.WARNING,
        messages: JSON.parse(errors).errors || [
          { message: 'Something went wrong' },
        ],
      })
    );

    return rejectWithValue(errors);
  }
};

export const updateUserNameAsync = createAsyncThunk(
  name + '/updateUserName',
  updateUserName
);

export const signUp: ApiMethod<SignIn, void> = async (data, { dispatch }) => {
  try {
    dispatch(
      CommonActions.setAlert({
        for: data.alert?.for,
        messages: null,
        show: false,
        type: null,
      })
    );

    dispatch(CommonActions.showLoading({ show: true }));

    const client = new Server.Server_Account_Client(
      undefined,
      publicIdentityClient
    );

    const response = await client.account_register_post_V1({
      ...data,
      deviceDetails,
    });

    if (response.success && response.data) {
      setUser({
        token: response.data.token ?? '',
        refreshToken: response.data.refreshToken ?? '',
        user: {
          id: response.data.id ?? '',
          name: response.data.name ?? '',
          email: response.data.email ?? '',
          isVerified: response.data.isVerified ?? false,
          picture: response.data.picture ?? '',
          autoPlayDisabled: response.data.autoPlayDisabled ?? false,
          nextTitleDisabled: response.data.nextTitleDisabled ?? false,
          isNewUser: response.data.isNewUser ?? false,
        },
      });

      dispatch(
        actions.setUser({
          token: response.data.token ?? '',
          refreshToken: response.data.refreshToken ?? '',
          user: {
            id: response.data.id ?? '',
            name: response.data.name ?? '',
            email: response.data.email ?? '',
            isVerified: response.data.isVerified ?? false,
            picture: response.data.picture ?? '',
            autoPlayDisabled: response.data.autoPlayDisabled ?? false,
            nextTitleDisabled: response.data.nextTitleDisabled ?? false,
            isNewUser: response.data.isNewUser ?? false,
          },
        })
      );
      dispatch(CommonActions.clearAlert());
    }
  } catch (error: any) {
    const errors = { ...error.response }.data;
    dispatch(
      CommonActions.setAlert({
        for: data.alert?.for,
        show: true,
        type: AlertType.WARNING,
        messages: JSON.parse(errors).errors || [
          { message: 'Something went wrong' },
        ],
      })
    );
  } finally {
    dispatch(CommonActions.hideLoading());
  }
};
export const signUpAsync = createAsyncThunk(name + '/signUp', signUp);

export const signUpUser: ApiMethod<SignIn, Server.AuthenticateOutput> = async (
  data,
  { dispatch, rejectWithValue }
) => {
  try {
    const client = new Server.Server_Account_Client(
      undefined,
      publicIdentityClient
    );

    const response = await client.account_register_post_V1(data);

    if (response.success && response.data) {
      return response.data;
    }

    return rejectWithValue(response.errors);
  } catch (error: any) {
    const errors = JSON.parse(error.response).errors;

    dispatch(
      CommonActions.setAlert({
        for: data.alert?.for,
        show: true,
        type: AlertType.ERROR,
        messages: errors || [{ message: 'Something went wrong' }],
      })
    );

    return rejectWithValue(errors);
  }
};
export const signUpUserAsync = createAsyncThunk(
  name + '/signUpUser',
  signUpUser
);

export const signUpUserAffiliate: ApiMethod<
  { data: SignIn; client: AxiosInstance },
  Server.AuthenticateOutput
> = async (input, { dispatch, rejectWithValue }) => {
  try {
    const client = new Server.Server_Account_Client(
      undefined,
      input.client || publicIdentityClient
    );

    const response = await client.account_register_post_V1(input.data);

    if (response.success && response.data) {
      return response.data;
    }

    return rejectWithValue(response.errors);
  } catch (error: any) {
    const errors = JSON.parse(error.response).errors;

    dispatch(
      CommonActions.setAlert({
        for: input.data.alert?.for,
        show: true,
        type: AlertType.ERROR,
        messages: errors || [{ message: 'Something went wrong' }],
      })
    );

    return rejectWithValue(errors);
  }
};
export const signUpUserAffiliateAsync = createAsyncThunk(
  name + '/signUpUserAffiliate',
  signUpUserAffiliate
);

export const signIn: ApiMethod<SignIn, void> = async (
  data,
  { dispatch, rejectWithValue }
) => {
  try {
    dispatch(
      CommonActions.setAlert({
        for: data.alert?.for,
        messages: null,
        show: false,
        type: null,
      })
    );

    dispatch(CommonActions.showLoading({ show: true }));

    const client = new Server.Server_Account_Client(
      undefined,
      publicIdentityClient
    );

    const response = await client.account_authenticate_post_V1({
      ...data,
      deviceDetails,
    });

    if (response.success && response.data) {
      setUser({
        token: response.data.token ?? '',
        refreshToken: response.data.refreshToken ?? '',
        user: {
          id: response.data.id ?? '',
          name: response.data.name ?? '',
          email: response.data.email ?? '',
          isVerified: response.data.isVerified ?? false,
          picture: response.data.picture ?? '',
          autoPlayDisabled: response.data.autoPlayDisabled ?? false,
          nextTitleDisabled: response.data.nextTitleDisabled ?? false,
        },
      });

      dispatch(
        actions.setUser({
          token: response.data.token ?? '',
          refreshToken: response.data.refreshToken ?? '',
          user: {
            id: response.data.id ?? '',
            name: response.data.name ?? '',
            email: response.data.email ?? '',
            isVerified: response.data.isVerified ?? false,
            picture: response.data.picture ?? '',
            autoPlayDisabled: response.data.autoPlayDisabled ?? false,
            nextTitleDisabled: response.data.nextTitleDisabled ?? false,
          },
        })
      );
      dispatch(CommonActions.clearAlert());
    }

    return rejectWithValue(response.errors);
  } catch (error: any) {
    const errors = JSON.parse(error.response).errors;

    dispatch(
      CommonActions.setAlert({
        for: 'page',
        type: AlertType.ERROR,
        messages: errors || [{ message: 'Something went wrong' }],
      })
    );

    return rejectWithValue(errors);
  } finally {
    dispatch(CommonActions.hideLoading());
  }
};
export const signInAsync = createAsyncThunk(name + '/signIn', signIn);

export const signInAffiliate: ApiMethod<
  { data: SignIn; client: AxiosInstance },
  void
> = async (input, { dispatch, rejectWithValue }) => {
  try {
    dispatch(
      CommonActions.setAlert({
        for: input.data.alert?.for,
        messages: null,
        show: false,
        type: null,
      })
    );

    dispatch(CommonActions.showLoading({ show: true }));

    const client = new Server.Server_Account_Client(
      undefined,
      input.client || publicIdentityClient
    );

    const response = await client.account_authenticate_post_V1({
      ...input.data,
      deviceDetails,
    });

    if (response.success && response.data) {
      setUser({
        token: response.data.token ?? '',
        refreshToken: response.data.refreshToken ?? '',
        user: {
          id: response.data.id ?? '',
          name: response.data.name ?? '',
          email: response.data.email ?? '',
          isVerified: response.data.isVerified ?? false,
          picture: response.data.picture ?? '',
          autoPlayDisabled: response.data.autoPlayDisabled ?? false,
          nextTitleDisabled: response.data.nextTitleDisabled ?? false,
        },
      });

      dispatch(
        actions.setUser({
          token: response.data.token ?? '',
          refreshToken: response.data.refreshToken ?? '',
          user: {
            id: response.data.id ?? '',
            name: response.data.name ?? '',
            email: response.data.email ?? '',
            isVerified: response.data.isVerified ?? false,
            picture: response.data.picture ?? '',
            autoPlayDisabled: response.data.autoPlayDisabled ?? false,
            nextTitleDisabled: response.data.nextTitleDisabled ?? false,
          },
        })
      );
      dispatch(CommonActions.clearAlert());
    }

    return rejectWithValue(response.errors);
  } catch (error: any) {
    const errors = JSON.parse(error.response).errors;

    dispatch(
      CommonActions.setAlert({
        for: 'page',
        type: AlertType.ERROR,
        messages: errors || [{ message: 'Something went wrong' }],
      })
    );

    return rejectWithValue(errors);
  } finally {
    dispatch(CommonActions.hideLoading());
  }
};
export const signInAffiliateAsync = createAsyncThunk(
  name + '/signInAffiliate',
  signInAffiliate
);

export const checkLoginOption: ApiMethod<
  Server.CheckLoginOptionInput,
  Server.CheckLoginOptionOutput,
  void
> = async (data, { dispatch, rejectWithValue }) => {
  return thunkErrorHandler(
    async () => {
      const client = new Server.Server_Account_Client(
        undefined,
        publicIdentityClient
      );

      const response = await client.account_checkLoginOption_post_V1(data);

      if (response.success && response.data) {
        return response.data;
      }

      return rejectWithValue(response.errors);
    },
    dispatch,
    rejectWithValue
  );
};
export const checkLoginOptionAsync = createAsyncThunk(
  name + '/checkLoginOption',
  checkLoginOption
);

export const signInB2b: ApiMethod<
  Server.AuthenticateB2BInput,
  Server.AuthenticateOutput,
  void
> = async (data, { dispatch, rejectWithValue }) => {
  return thunkErrorHandler(
    async () => {
      const client = new Server.Server_Account_Client(
        undefined,
        publicIdentityClient
      );

      const response = await client.account_b2b_authenticate_post_V1({
        ...data,
        deviceDetails,
      });

      if (response.success && response.data) {
        return response.data;
      }

      return rejectWithValue(response.errors);
    },
    dispatch,
    rejectWithValue
  );
};
export const signInB2bAsync = createAsyncThunk(name + '/signInB2b', signInB2b);

export const signInWithGoogle: ApiMethod<
  SignInWithGoogle,
  Server.AuthenticateOutput | null,
  any
> = async (data, { dispatch, rejectWithValue }) => {
  try {
    dispatch(
      CommonActions.setAlert({
        for: data.alert?.for,
        messages: null,
        show: false,
        type: null,
      })
    );

    dispatch(CommonActions.showLoading({ show: true }));

    const client = new Server.Server_Account_Client(
      undefined,
      publicIdentityClient
    );
    const response = await client.account_authenticate_google_post_V1({
      token: data.token,
      deviceDetails,
    });

    if (response.success && response.data) {
      setUser({
        token: response.data.token ?? '',
        refreshToken: response.data.refreshToken ?? '',
        user: {
          id: response.data.id ?? '',
          name: response.data.name ?? '',
          email: response.data.email ?? '',
          isVerified: response.data.isVerified ?? false,
          picture: response.data.picture ?? '',
          autoPlayDisabled: response.data.autoPlayDisabled ?? false,
          nextTitleDisabled: response.data.nextTitleDisabled ?? false,
          isNewUser: response.data.isNewUser ?? false,
        },
      });
      dispatch(
        actions.setUser({
          token: response.data.token ?? '',
          refreshToken: response.data.refreshToken ?? '',
          user: {
            id: response.data.id ?? '',
            name: response.data.name ?? '',
            email: response.data.email ?? '',
            isVerified: response.data.isVerified ?? false,
            picture: response.data.picture ?? '',
            autoPlayDisabled: response.data.autoPlayDisabled ?? false,
            nextTitleDisabled: response.data.nextTitleDisabled ?? false,
            isNewUser: response.data.isNewUser ?? false,
          },
        })
      );
      dispatch(CommonActions.clearAlert());
      return response.data;
    }
    return rejectWithValue(response.errors);
  } catch (error: any) {
    // Show alert
    const errors = { ...error.response }.data;
    dispatch(
      CommonActions.setAlert({
        for: data.alert?.for,
        show: true,
        type: AlertType.WARNING,
        messages: JSON.parse(errors).errors || [
          { message: 'Something went wrong' },
        ],
      })
    );
    return rejectWithValue(error);
  } finally {
    dispatch(CommonActions.resetLoading());
  }
};
export const signInWithGoogleAsync = createAsyncThunk(
  name + '/signInWithGoogle',
  signInWithGoogle
);

export const signInWithGoogleAccessToken: ApiMethod<
  SignInWithGoogle,
  Server.AuthenticateOutput | null,
  any
> = async (data, { dispatch, rejectWithValue }) => {
  try {
    dispatch(
      CommonActions.setAlert({
        for: data.alert?.for,
        messages: null,
        show: false,
        type: null,
      })
    );

    dispatch(CommonActions.showLoading({ show: true }));

    const client = new Server.Server_Account_Client(
      undefined,
      publicIdentityClient
    );
    const response = await client.account_authenticate_google_post_V2({
      token: data.token,
      deviceDetails,
    });

    if (response.success && response.data) {
      setUser({
        token: response.data.token ?? '',
        refreshToken: response.data.refreshToken ?? '',
        user: {
          id: response.data.id ?? '',
          name: response.data.name ?? '',
          email: response.data.email ?? '',
          isVerified: response.data.isVerified ?? false,
          picture: response.data.picture ?? '',
          autoPlayDisabled: response.data.autoPlayDisabled ?? false,
          nextTitleDisabled: response.data.nextTitleDisabled ?? false,
          isNewUser: response.data.isNewUser ?? false,
        },
      });
      dispatch(
        actions.setUser({
          token: response.data.token ?? '',
          refreshToken: response.data.refreshToken ?? '',
          user: {
            id: response.data.id ?? '',
            name: response.data.name ?? '',
            email: response.data.email ?? '',
            isVerified: response.data.isVerified ?? false,
            picture: response.data.picture ?? '',
            autoPlayDisabled: response.data.autoPlayDisabled ?? false,
            nextTitleDisabled: response.data.nextTitleDisabled ?? false,
            isNewUser: response.data.isNewUser ?? false,
          },
        })
      );
      dispatch(CommonActions.clearAlert());
      return response.data;
    }
    return rejectWithValue(response.errors);
  } catch (error: any) {
    // Show alert
    const errors = { ...error.response }.data;
    dispatch(
      CommonActions.setAlert({
        for: data.alert?.for,
        show: true,
        type: AlertType.WARNING,
        messages: JSON.parse(errors).errors || [
          { message: 'Something went wrong' },
        ],
      })
    );
    return rejectWithValue(error);
  } finally {
    dispatch(CommonActions.resetLoading());
  }
};
export const signInWithGoogleAccessTokenAsync = createAsyncThunk(
  name + '/signInWithGoogleAccessToken',
  signInWithGoogleAccessToken
);

export const signInWithFacebook: ApiMethod<
  SignInWithFacebook,
  Server.AuthenticateOutput
> = async (data, { dispatch, rejectWithValue }) => {
  try {
    dispatch(
      CommonActions.setAlert({
        for: data.alert?.for,
        messages: null,
        show: false,
        type: null,
      })
    );

    dispatch(CommonActions.showLoading({ show: true }));

    const client = new Server.Server_Account_Client(
      undefined,
      publicIdentityClient
    );
    const response = await client.account_authenticate_facebook_post_V1({
      token: data.token,
      deviceDetails,
    });

    if (response.success && response.data) {
      setUser({
        token: response.data.token ?? '',
        refreshToken: response.data.refreshToken ?? '',
        user: {
          id: response.data.id ?? '',
          name: response.data.name ?? '',
          email: response.data.email ?? '',
          isVerified: response.data.isVerified ?? false,
          picture: response.data.picture ?? '',
          autoPlayDisabled: response.data.autoPlayDisabled ?? false,
          nextTitleDisabled: response.data.nextTitleDisabled ?? false,
          isNewUser: response.data.isNewUser ?? false,
        },
      });
      dispatch(
        actions.setUser({
          token: response.data.token ?? '',
          refreshToken: response.data.refreshToken ?? '',
          user: {
            id: response.data.id ?? '',
            name: response.data.name ?? '',
            email: response.data.email ?? '',
            isVerified: response.data.isVerified ?? false,
            picture: response.data.picture ?? '',
            autoPlayDisabled: response.data.autoPlayDisabled ?? false,
            nextTitleDisabled: response.data.nextTitleDisabled ?? false,
            isNewUser: response.data.isNewUser ?? false,
          },
        })
      );
      dispatch(CommonActions.clearAlert());
      return response.data;
    }
    return rejectWithValue(response.errors);
  } catch (error: any) {
    console.log(error);
    // Show alert
    dispatch(
      CommonActions.setAlert({
        for: data.alert?.for,
        show: true,
        type: AlertType.WARNING,
        messages: error.response?.data?.errors || [
          { message: 'Something went wrong' },
        ],
      })
    );
    return rejectWithValue(error);
  } finally {
    dispatch(CommonActions.resetLoading());
  }
};
export const signInWithFacebookAsync = createAsyncThunk(
  name + '/signInWithFacebook',
  signInWithFacebook
);

export const signInWithApple: ApiMethod<SignInWithApple, void> = async (
  data,
  { dispatch }
) => {
  try {
    dispatch(
      CommonActions.setAlert({
        for: data.alert?.for,
        messages: null,
        show: false,
        type: null,
      })
    );

    dispatch(CommonActions.showLoading({ show: true }));

    const client = new Server.Server_Account_Client(
      undefined,
      publicIdentityClient
    );
    const response = await client.account_authenticate_apple_post_V1({
      token: data.token,
      deviceDetails,
    });

    if (response.success && response.data) {
      setUser({
        token: response.data.token ?? '',
        refreshToken: response.data.refreshToken ?? '',
        user: {
          id: response.data.id ?? '',
          name: response.data.name ?? '',
          email: response.data.email ?? '',
          isVerified: response.data.isVerified ?? false,
          picture: response.data.picture ?? '',
          autoPlayDisabled: response.data.autoPlayDisabled ?? false,
          nextTitleDisabled: response.data.nextTitleDisabled ?? false,
          isNewUser: response.data.isNewUser ?? false,
        },
      });
      dispatch(
        actions.setUser({
          token: response.data.token ?? '',
          refreshToken: response.data.refreshToken ?? '',
          user: {
            id: response.data.id ?? '',
            name: response.data.name ?? '',
            email: response.data.email ?? '',
            isVerified: response.data.isVerified ?? false,
            picture: response.data.picture ?? '',
            autoPlayDisabled: response.data.autoPlayDisabled ?? false,
            nextTitleDisabled: response.data.nextTitleDisabled ?? false,
            isNewUser: response.data.isNewUser ?? false,
          },
        })
      );
      dispatch(CommonActions.clearAlert());
    }
  } catch (error: any) {
    console.log(error);
    // Show alert
    dispatch(
      CommonActions.setAlert({
        for: data.alert?.for,
        show: true,
        type: AlertType.WARNING,
        messages: error.response?.data?.errors || [
          { message: 'Something went wrong' },
        ],
      })
    );
  } finally {
    dispatch(CommonActions.resetLoading());
  }
};
export const signInWithAppleAsync = createAsyncThunk(
  name + '/signInWithApple',
  signInWithApple
);

export const getTwitterBrowserLink: ApiMethod<
  TwitterBrowserLink,
  void
> = async (data, { dispatch }) => {
  try {
    dispatch(
      CommonActions.setAlert({
        for: data.alert?.for,
        messages: null,
        show: false,
        type: null,
      })
    );

    dispatch(CommonActions.showLoading({ show: true }));

    const client = new Server.Server_Account_Client(
      undefined,
      publicIdentityClient
    );
    const response =
      await client.account_authenticate_twitterBrowserLink_post_V1({
        callbackUrl: data.callbackUrl,
      });

    if (response.success && response.data) {
      window.location.replace(response.data.url || '');

      dispatch(CommonActions.clearAlert());
    }
  } catch (error: any) {
    const errors = { ...error.response }.data;

    dispatch(
      CommonActions.setAlert({
        for: data.alert?.for,
        show: true,
        type: AlertType.WARNING,
        messages: JSON.parse(errors).errors || [
          { message: 'Something went wrong' },
        ],
      })
    );

    dispatch(CommonActions.hideLoading());
  }
};
export const getTwitterBrowserLinkAsync = createAsyncThunk(
  name + '/getTwitterBrowserLink',
  getTwitterBrowserLink
);

export const signInWithTwitter: ApiMethod<SignInWithTwitter, void> = async (
  data,
  { dispatch }
) => {
  try {
    dispatch(
      CommonActions.setAlert({
        for: data.alert?.for,
        messages: null,
        show: false,
        type: null,
      })
    );

    dispatch(CommonActions.showLoading({ show: true }));

    const client = new Server.Server_Account_Client(
      undefined,
      publicIdentityClient
    );
    const response = await client.account_authenticate_twitter_post_V2({
      oauthToken: data.oauthToken,
      oauthVerifier: data.oauthVerifier,
      deviceDetails,
    });

    if (response.success && response.data) {
      setUser({
        token: response.data.token ?? '',
        refreshToken: response.data.refreshToken ?? '',
        user: {
          id: response.data.id ?? '',
          name: response.data.name ?? '',
          email: response.data.email ?? '',
          isVerified: response.data.isVerified ?? false,
          picture: response.data.picture ?? '',
          autoPlayDisabled: response.data.autoPlayDisabled ?? false,
          nextTitleDisabled: response.data.nextTitleDisabled ?? false,
          isNewUser: response.data.isNewUser ?? false,
        },
      });
      dispatch(
        actions.setUser({
          token: response.data.token ?? '',
          refreshToken: response.data.refreshToken ?? '',
          user: {
            id: response.data.id ?? '',
            name: response.data.name ?? '',
            email: response.data.email ?? '',
            isVerified: response.data.isVerified ?? false,
            picture: response.data.picture ?? '',
            autoPlayDisabled: response.data.autoPlayDisabled ?? false,
            nextTitleDisabled: response.data.nextTitleDisabled ?? false,
            isNewUser: response.data.isNewUser ?? false,
          },
        })
      );
      dispatch(CommonActions.clearAlert());
    }
  } catch (error: any) {
    // Show alert
    const errors = { ...error.response }.data;

    dispatch(
      CommonActions.setAlert({
        for: data.alert?.for,
        show: true,
        type: AlertType.WARNING,
        messages: JSON.parse(errors).errors || [
          { message: 'Something went wrong' },
        ],
      })
    );
  } finally {
    dispatch(CommonActions.resetLoading());
  }
};
export const signInWithTwitterAsync = createAsyncThunk(
  name + '/signInWithTwitter',
  signInWithTwitter
);

export const signOut: ApiMethod<never, void> = async (_, { dispatch }) => {
  try {
    removeUser();
    dispatch(actions.resetAuth());
    // dispatch(actions.initUrl('/home'));
    // window.location.reload();
  } catch (error: any) {
    // Show alert
    const errors = { ...error.response }.data;
    dispatch(
      CommonActions.setAlert({
        show: true,
        type: AlertType.WARNING,
        messages: JSON.parse(errors).errors || [
          { message: 'Something went wrong' },
        ],
      })
    );
  } finally {
    dispatch(CommonActions.resetLoading());
  }
};

export const signOutAsync = createAsyncThunk(name + '/signOut', signOut);

export const requestPasswordReset: ApiMethod<
  RequestPasswordReset,
  void
> = async (data, { dispatch, rejectWithValue }) => {
  try {
    dispatch(
      CommonActions.setAlert({
        for: data.alert?.for,
        messages: null,
        show: false,
        type: null,
      })
    );

    dispatch(CommonActions.showLoading({ show: true }));

    const client = new Server.Server_Account_Client(
      undefined,
      publicIdentityClient
    );
    const response = await client.account_passwordReset_request_post_V1({
      email: data.email,
    });

    if (response.success) {
      dispatch(
        CommonActions.setAlert({
          show: true,
          type: AlertType.SUCCESS,
          messages: [
            {
              code: 100,
              field: null,
              message:
                data.successMessage || 'The Code has been sent to your email',
            },
          ],
        })
      );

      return;
    } else {
      return rejectWithValue(response.errors);
    }
  } catch (error: any) {
    // Show alert
    dispatch(
      CommonActions.setAlert({
        for: data.alert?.for,
        show: true,
        type: AlertType.WARNING,
        messages: JSON.parse(error.response)?.errors || [
          { message: 'Something went wrong' },
        ],
      })
    );

    return rejectWithValue(error);
  } finally {
    dispatch(CommonActions.resetLoading());
  }
};
export const requestPasswordResetAsync = createAsyncThunk(
  name + '/requestPasswordReset',
  requestPasswordReset
);

export const verifyPasswordResetCode: ApiMethod<
  VerifyPasswordResetCode,
  void
> = async (data, { dispatch, rejectWithValue }) => {
  try {
    dispatch(
      CommonActions.setAlert({
        for: data.alert?.for,
        messages: null,
        show: false,
        type: null,
      })
    );

    dispatch(CommonActions.showLoading({ show: true }));

    const client = new Server.Server_Account_Client(
      undefined,
      publicIdentityClient
    );
    const response = await client.account_passwordReset_verify_post_V1({
      email: data.email,
      code: data.code,
    });

    if (response.success) {
      dispatch(
        CommonActions.setAlert({
          show: true,
          type: AlertType.SUCCESS,
          messages: [
            {
              code: 100,
              field: null,
              message:
                data.successMessage ||
                'Successful password reset code verification',
            },
          ],
        })
      );

      return;
    } else {
      return rejectWithValue(response.errors);
    }
  } catch (error: any) {
    // Show alert
    dispatch(
      CommonActions.setAlert({
        for: data.alert?.for,
        show: true,
        type: AlertType.WARNING,
        messages: JSON.parse(error.response)?.errors || [
          { message: 'Something went wrong' },
        ],
      })
    );

    return rejectWithValue(error);
  } finally {
    dispatch(CommonActions.resetLoading());
  }
};
export const verifyPasswordResetCodeAsync = createAsyncThunk(
  name + '/verifyPasswordResetCode',
  verifyPasswordResetCode
);

export const resetPassword: ApiMethod<ResetPassword, void> = async (
  data,
  { dispatch, rejectWithValue }
) => {
  try {
    dispatch(
      CommonActions.setAlert({
        for: data.alert?.for,
        messages: null,
        show: false,
        type: null,
      })
    );

    dispatch(CommonActions.showLoading({ show: true }));

    const client = new Server.Server_Account_Client(
      undefined,
      publicIdentityClient
    );
    const response = await client.account_passwordReset_reset_post_V1({
      email: data.email,
      code: data.code,
      password: data.password,
    });

    if (response.success) {
      dispatch(
        CommonActions.setAlert({
          show: true,
          type: AlertType.SUCCESS,
          messages: [
            {
              code: 100,
              field: null,
              message:
                data.successMessage ||
                'Successful password reset code verification',
            },
          ],
        })
      );

      return;
    } else {
      return rejectWithValue(response.errors);
    }
  } catch (error: any) {
    // Show alert
    dispatch(
      CommonActions.setAlert({
        for: data.alert?.for,
        show: true,
        type: AlertType.WARNING,
        messages: JSON.parse(error.response)?.errors || [
          { message: 'Something went wrong' },
        ],
      })
    );

    return rejectWithValue(error);
  } finally {
    dispatch(CommonActions.resetLoading());
  }
};
export const resetPasswordAsync = createAsyncThunk(
  name + '/resetPassword',
  resetPassword
);

export const checkEmail: ApiMethod<string, void> = async (
  email,
  { dispatch }
) => {
  try {
    dispatch(CommonActions.showLoading({ show: true }));

    const client = new Server.Server_Account_Client(
      undefined,
      publicIdentityClient
    );
    const response = await client.account_checkEmail_get_V1(email);

    if (response.success) {
      console.log(response);
    }
  } catch (error: any) {
    console.log(error);
    const errors = { ...error.response }.data;
    // Show alert

    dispatch(
      CommonActions.setAlert({
        show: true,
        type: AlertType.WARNING,
        messages: JSON.parse(errors).data?.errors || [
          { message: 'Something went wrong' },
        ],
      })
    );
  } finally {
    dispatch(CommonActions.resetLoading());
  }
};
export const checkEmailAsync = createAsyncThunk(
  name + '/checkEmail',
  checkEmail
);

export const requestWhatsappOtp: ApiMethod<
  Server.RequestWhatsAppOTPInput & { alert: { for: 'modal' | 'page' } },
  Server.RequestWhatsAppOTPOutput,
  void
> = async (data, { dispatch, rejectWithValue }) => {
  return thunkErrorHandler(
    async () => {
      return thunkResponseCheckerSdk(async () => {
        const client = new Server.Server_Account_Client(
          undefined,
          publicIdentityClient
        );

        return client.account_requestWhatsappOtp_post_V1(data);
      }, rejectWithValue);
    },
    dispatch,
    rejectWithValue
  );
};

export const requestWhatsappOtpAsync = createAsyncThunk(
  name + '/requestWhatsappOtp',
  requestWhatsappOtp
);

export const signInWithWhatsapp: ApiMethod<
  Server.WhatsAppLoginInput & { alert: { for: 'modal' | 'page' } },
  Server.AuthenticateOutput,
  void
> = async (data, { dispatch, rejectWithValue }) => {
  return thunkErrorHandler(
    async () => {
      dispatch(CommonActions.showLoading({ show: true }));

      const response = await thunkResponseCheckerSdk(async () => {
        const client = new Server.Server_Account_Client(
          undefined,
          publicIdentityClient
        );

        const response = await client.account_authenticate_whatsapp_post_V1({
          ...data,
          deviceDetails,
        });

        dispatch(CommonActions.resetLoading());

        return response;
      }, rejectWithValue);

      try {
        console.log(response);
        setUser({
          token: response.token ?? '',
          refreshToken: response.refreshToken ?? '',
          user: {
            id: response.id ?? '',
            name: response.name ?? '',
            email: response.email ?? '',
            isVerified: response.isVerified ?? false,
            picture: response.picture ?? '',
            autoPlayDisabled: response.autoPlayDisabled ?? false,
            nextTitleDisabled: response.nextTitleDisabled ?? false,
            isNewUser: response.isNewUser ?? false,
            phoneNumber: response.phoneNumber ?? '',
          },
        });
        console.log('after setting user');

        dispatch(
          actions.setUser({
            token: response.token ?? '',
            refreshToken: response.refreshToken ?? '',
            user: {
              id: response.id ?? '',
              name: response.name ?? '',
              email: response.email ?? '',
              isVerified: response.isVerified ?? false,
              picture: response.picture ?? '',
              autoPlayDisabled: response.autoPlayDisabled ?? false,
              nextTitleDisabled: response.nextTitleDisabled ?? false,
              isNewUser: response.isNewUser ?? false,
              phoneNumber: response.phoneNumber ?? '',
            },
          })
        );
        dispatch(CommonActions.clearAlert());
      } catch (error: any) {
        console.log(error);
      }

      return response;
    },
    dispatch,
    rejectWithValue
  );
};

export const signInWithWhatsappAsync = createAsyncThunk(
  name + '/signInWithWhatsapp',
  signInWithWhatsapp
);
